import { Link } from "react-router-dom";
// Components
import InfoSection from "../../shared/InfoSection";
// Images
import orgmembers from "../../assets/screenshots/members-pages/org-members.png";
import orgmembersapprove from "../../assets/screenshots/members-pages/org-members-approve.png";
import memberdirectory from "../../assets/screenshots/members-pages/member-directory.png";
import orginsightsactivity from "../../assets/screenshots/insights-pages/org-insights-activity.png";
import memberprofile from "../../assets/screenshots/profile-pages/sample-profile.png";
// Icons
import { CheckIcon, ViewGridIcon } from "@heroicons/react/outline";
import { IoIosPeople, IoMdThumbsUp, IoIosTrendingUp } from "react-icons/io";

const features = [
  {
    name: "Home for your Members",
    description:
      "Send your members to your community page to signup and learn more about what your community has to offer.",
  },
  {
    name: "Member Directory",
    description:
      "Offer members the ability to learn more about each other and find people to network.",
  },
  {
    name: "View Members in One Place",
    description:
      "See a convenient list of all your community members and potential members.",
  },
  {
    name: "Ability to Approve Members",
    description:
      "Keep track of member statuses and members that are pending approval, approved, and denied entry into your community.",
  },
  {
    name: "Moderation Tools",
    description:
      "Have control to ban and remove members to keep your community safe.",
  },
  {
    name: "Demographic Insights",
    description:
      "Learn more about your member's background including gender, race, school, education level, interests, skills, and more.",
  },
  {
    name: "Member Signup and Activity",
    description:
      "Keep track of your community's activity, inactivity, latest signups, and growth.",
  },
  {
    name: "Newsletters",
    description:
      "Send custom newsletters to your members notifying them about new events, programs, opportunities, and community updates.",
    comingSoon: true,
  },
];

export default function FeaturesMembers() {
  return (
    <div className="bg-white">
      <main>
        {/* Hero section */}
        <div className="bg-gradient-to-br from-primary-100 via-white to-pink-100 pb-8 sm:pb-12 lg:pb-12">
          <div className="pt-8 overflow-hidden sm:pt-12 lg:relative lg:py-48">
            <div className="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl lg:grid lg:grid-cols-2 lg:gap-24">
              <div>
                <div className="mt-20">
                  <div>
                    <span className="rounded bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-600 tracking-wide uppercase">
                      Members
                    </span>
                  </div>
                  <div className="mt-6 sm:max-w-xl">
                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">
                      🙋 Manage members
                    </h1>
                    <p className="mt-6 text-xl text-gray-500">
                      Our member management tools allow you to conveniently
                      contact, approve, and learn more about your members.
                    </p>
                    <div className="mt-4 md:mt-8">
                      <Link
                        to="/signup?redirect=/org/new"
                        className="px-4 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-500 bg-opacity-90 hover:bg-opacity-80 sm:px-8"
                      >
                        Get started
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sm:mx-auto sm:max-w-3xl sm:px-6">
              <div className="py-12 sm:relative sm:mt-12 sm:py-16 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
                <div className="hidden sm:block">
                  <svg
                    className="absolute top-8 right-1/2 -mr-3 lg:m-0 lg:left-0"
                    width={404}
                    height={392}
                    fill="none"
                    viewBox="0 0 404 392"
                  >
                    <defs>
                      <pattern
                        id="837c3e70-6c3a-44e6-8854-cc48c737b659"
                        x={0}
                        y={0}
                        width={20}
                        height={20}
                        patternUnits="userSpaceOnUse"
                      >
                        <rect
                          x={0}
                          y={0}
                          width={4}
                          height={4}
                          className="text-gray-200"
                          fill="currentColor"
                        />
                      </pattern>
                    </defs>
                    <rect
                      width={404}
                      height={392}
                      fill="url(#837c3e70-6c3a-44e6-8854-cc48c737b659)"
                    />
                  </svg>
                </div>
                <div className="relative pl-4 -mr-40 sm:mx-auto sm:max-w-3xl sm:px-0 lg:max-w-none lg:h-full lg:pl-12">
                  <img
                    className="w-full rounded-md shadow-xl ring-1 ring-black ring-opacity-5 lg:h-full lg:w-auto lg:max-w-none"
                    src={orgmembers}
                    alt="org members"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <InfoSection
            title="Provide opportunities for your members to network"
            subtitle="Allow members to find others in your community through your own member directory."
            image={memberdirectory}
            icon={ViewGridIcon}
            topText="Directory"
            textLeft
            color="primary"
            secondaryColor="pink"
          />
          <InfoSection
            title="Discover more about your community members"
            subtitle="Learn more about each member in your community with convenient access to in-depth member profiles."
            image={memberprofile}
            icon={IoIosPeople}
            topText="Profiles"
            textLeft={false}
            bggray
            color="primary"
            secondaryColor="pink"
          />
          <InfoSection
            title="Gain deep insights into your members"
            subtitle="Analyze member signups and activity. See your diversity in action through breakdowns of gender, race, school, and more."
            image={orginsightsactivity}
            icon={IoIosTrendingUp}
            topText="Insights"
            textLeft
            color="primary"
            secondaryColor="pink"
          />
          <InfoSection
            title="Access controls, permissions, and moderation"
            subtitle="Keep parts of your community private, manage approval processes, and moderation tools to keep your community safe."
            image={orgmembersapprove}
            icon={IoMdThumbsUp}
            topText="Manage"
            textLeft={false}
            bggray
            color="primary"
            secondaryColor="pink"
          />
        </div>

        {/* Black Bg Feature Section */}
        <div className="bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 sm:pt-20 sm:pb-24 lg:max-w-7xl lg:pt-24 lg:px-8">
            <h2 className="text-center text-3xl md:text-4xl font-extrabold text-gray-100 tracking-tight">
              Features that you and your members will love
            </h2>
            <div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:mt-16 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-16">
              {features.map((feature) => (
                <div key={feature.name} className="relative">
                  <dt>
                    <CheckIcon
                      className="absolute h-6 w-6 text-green-500"
                      aria-hidden="true"
                    />
                    <p className="ml-9 text-lg leading-6 font-semibold text-gray-100">
                      {feature.name}{" "}
                      {feature.comingSoon && (
                        <span className="inline-flex items-center ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-200 text-pink-800">
                          Coming Soon
                        </span>
                      )}
                    </p>
                  </dt>
                  <dd className="mt-2 ml-9 text-base text-gray-400">
                    {feature.description}
                  </dd>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto text-center py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              <span className="block">Ready to dive in?</span>
              <span className="block">Start inviting your members today.</span>
            </h2>
            <div className="mt-8 flex justify-center">
              <div className="inline-flex rounded-md shadow">
                <Link
                  to="/signup?redirect=/org/new"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600"
                >
                  Get started
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
